/* eslint react/no-danger: 0 */
import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import { useLabels, usePrefs, useRequestData, useFlags } from 'wsm-common-data';
import { trackEvent } from 'ddc-track-event';
import { transformAddData } from 'wsm-contact-transformer';
import {
	shouldUseOffsiteVdpUrl,
	setClassNames,
	getValidHttpsUrl
} from '../utils/utility';
import Distance from './Distance';
import Delivery from './Delivery';
import { useLocation } from '../hooks/useLocation';
import { useLocationState } from '../features/location';
import { useWidgetInfo } from '../features/widgetInfo';
import LocationStatus from './LocationStatus';

const Location = ({ displayObj }) => {
	const {
		icon,
		displayStatusElement,
		locationElement,
		displayLink,
		linkLabel,
		dataWidth,
		href,
		relData,
		dataHref,
		dataTitle,
		isMobile,
		isInTransit,
		status
	} = displayObj;

	const { windowId, widgetName } = useRequestData();
	const anchorRef = useRef();

	const labels = useLabels();
	const {
		linkToChildVDP,
		deliveryEnabled,
		legacyStyling,
		enableWebsiteLink,
		enableLocationLink,
		enablePhoneNumber,
		showWebSiteLocation,
		widgetClasses
	} = usePrefs();
	const flags = useFlags();

	const offSiteVDPFlag = flags['ws-vehicle-location-offsite-vdp'] || false;

	const { uri, selectedAccountId, accountId } = useWidgetInfo();
	const locationObj = useLocationState();

	const enablePathToChildVDP =
		linkToChildVDP === 'true' && locationObj.isOnPlatformAccount;
	const enableTransferFee = deliveryEnabled === 'true';
	const enableNewUI = legacyStyling === 'false';
	const isNewVDP = widgetClasses?.includes('vdp-responsive-section-location');

	const options = {
		displaySiteUrl: !!selectedAccountId && selectedAccountId !== accountId,
		pagePath: enablePathToChildVDP ? uri : ''
	};

	const { phoneObj = {} } = locationObj;
	const callTrackingNumber = `${labels.get('CALL')} ${phoneObj.number}`;

	let showWebsiteLink;
	if (
		shouldUseOffsiteVdpUrl(
			showWebSiteLocation,
			linkToChildVDP,
			offSiteVDPFlag,
			locationObj?.offsiteVdpUrl
		)
	) {
		showWebsiteLink = locationObj.offsiteVdpUrl;
	} else {
		const { dealerInfo } = transformAddData(locationObj, options);
		showWebsiteLink = dealerInfo?.url?.link;
	}

	showWebsiteLink = getValidHttpsUrl(showWebsiteLink);

	let { displayStatusClass, linkClass, vehicleStatusDisplay } = displayObj;
	const { location: locationDefault } = displayObj;

	const { location } = useLocation(locationDefault);

	let locationName = location?.name;

	if (
		!enableNewUI &&
		vehicleStatusDisplay?.toLowerCase().includes('on the lot') &&
		!locationName?.toLowerCase().includes('at')
	) {
		locationName = `at ${locationName}`;
	}

	if (displayStatusElement === 'strong') {
		vehicleStatusDisplay = <strong>{vehicleStatusDisplay}</strong>;
	}

	if (locationElement === 'strong') {
		locationName = `<strong>${locationName}</strong>`;
	}

	const iconColorClass = icon === 'lock' ? 'text-danger' : '';
	const iconSizeClass = isMobile
		? 'ddc-font-size-xxlarge'
		: 'ddc-font-size-xlarge';
	const iconWrapperClass = setClassNames(
		'ddc-icon',
		`ddc-icon-${icon}`,
		iconColorClass,
		iconSizeClass
	);
	const iconWebsiteClasses = setClassNames(
		'ddc-icon',
		'ddc-icon-launch',
		isMobile ? 'ddc-font-size-xlarge' : 'ddc-font-size-large mb-2',
		'text-muted',
		'ml-3'
	);
	const locationMarginClass = isMobile ? 'my-0' : 'mr-3 pr-2';
	const locationClass = setClassNames('d-sm-inline', locationMarginClass);
	const desktopTransferFeeClass = enableTransferFee ? 'border-right-0' : '';

	const transferFeeClass = setClassNames(
		'p-0',
		'mb-4',
		'mr-4',
		desktopTransferFeeClass
	);

	const transferFeeBorderClass = setClassNames(
		'pb-xl-0',
		'mb-xl-0',
		'mh-24',
		'd-flex',
		'flex-wrap',
		'align-items-center',
		'align-content-center',
		'pt-2'
	);

	const displayStatusMarginClass = isMobile ? 'my-0' : '';
	displayStatusClass = setClassNames(
		displayStatusMarginClass,
		displayStatusClass
	);

	const createMarkup = (encodedLocation) => {
		return { __html: `${encodedLocation}` };
	};

	linkClass = isMobile
		? setClassNames('d-inline-block', 'py-0', linkClass)
		: linkClass;
	linkClass = showWebsiteLink
		? setClassNames('position-static', linkClass)
		: linkClass;
	return (
		<>
			{enableNewUI ? (
				<div
					className={setClassNames(
						'vehicle-location-wrapper',
						'd-flex',
						'align-items-start',
						'line-height-condensed',
						'm-0',
						'bg-none',
						'flex-wrap',
						!isMobile && !isNewVDP ? 'pt-4' : '' // add spacing for old VDP desktop only
					)}
				>
					<div className={transferFeeClass}>
						<LocationStatus
							locationName={locationName}
							status={status}
						/>
						<div className={transferFeeBorderClass}>
							<Distance />
							<span className="ddc-font-size-xsmall">
								{displayLink &&
									enableLocationLink === 'true' && (
										<a
											className={`text-muted ${linkClass}`}
											href={href}
											rel={relData}
											data-width={dataWidth}
											data-href={dataHref}
											data-title={dataTitle}
										>
											<span className="text-decoration-underline">
												{linkLabel}
											</span>
										</a>
									)}
								{!!showWebsiteLink &&
									enableWebsiteLink === 'true' && (
										<div className="d-inline-flex align-items-baseline">
											<a
												target="_blank"
												rel="noopener noreferrer"
												href={showWebsiteLink}
												className="d-inline-flex align-items-center text-decoration-none text-muted"
												data-title={labels.get(
													'ACCOUNT_LINK_WEBSITE'
												)}
											>
												<span className="text-decoration-underline">
													{labels.get(
														'ACCOUNT_LINK_WEBSITE'
													)}
												</span>
												<i
													className={
														iconWebsiteClasses
													}
												/>
											</a>
										</div>
									)}
							</span>
						</div>
					</div>
					<div
						className={`${
							enableTransferFee && phoneObj.number
								? 'col-xs-12 p-0 d-flex flex-wrap rg-16'
								: ''
						}`}
					>
						{enableTransferFee && (
							<div
								className={`
								px-0 d-flex ${isMobile ? 'pb-3' : 'px-0'}`}
							>
								<span
									className={`
								border-right pr-4 text-nowrap ${desktopTransferFeeClass}`}
								>
									<div>
										<strong>
											{labels.get('FREE_PICK_UP')}
										</strong>
									</div>
									<div className="ddc-font-size-xsmall text-muted pt-2 pt-lg-2 mh-24 d-flex align-items-center">
										{labels.get('GET_IT_TODAY')}
									</div>
								</span>
								{!isInTransit && <Delivery />}
							</div>
						)}
						{phoneObj?.number && enablePhoneNumber === 'true' && (
							<div className="p-0">
								<div>
									{isMobile ? (
										<a
											href={`tel:${phoneObj.number}`}
											className="d-inline-flex align-items-center text-link text-decoration-underline"
											onClick={() =>
												trackEvent(
													widgetName,
													windowId,
													{
														element: 'Phone number',
														elementCTA:
															phoneObj.dept,
														result: 'Call interstitial presented'
													}
												)
											}
											ref={anchorRef}
											data-click-to-call={phoneObj.dept}
											data-click-to-call-phone={
												phoneObj.number
											}
											data-phone-ref={phoneObj.type?.toUpperCase()}
											data-account-ref={selectedAccountId}
										>
											<strong>
												{callTrackingNumber}
											</strong>
										</a>
									) : (
										<span
											data-phone-ref={phoneObj.type?.toUpperCase()}
											data-account-ref={selectedAccountId}
										>
											<strong>
												{callTrackingNumber}
											</strong>
										</span>
									)}
								</div>
								<div className="ddc-font-size-xsmall text-muted pt-2 pt-lg-2 mh-24 d-flex align-items-center">
									{labels.get('WE_HERE_TO_HELP')}
								</div>
							</div>
						)}
					</div>
				</div>
			) : (
				<div
					className={setClassNames(
						'vehicle-location-wrapper',
						'box',
						'box-border',
						'd-flex',
						'align-items-start',
						'line-height-condensed',
						'm-0',
						'p-3',
						'p-sm-4',
						'legacy-styling'
					)}
				>
					<div className="pr-3 pl-2 pl-sm-0">
						<i className={iconWrapperClass} />
					</div>
					<div className="py-2 py-sm-0 d-flex flex-column">
						<div className={displayStatusClass}>
							{vehicleStatusDisplay}{' '}
						</div>
						<div className={locationClass}>
							<span
								dangerouslySetInnerHTML={createMarkup(
									locationName
								)}
							/>
						</div>
						<div className="d-inline-flex align-items-baseline">
							{displayLink && (
								<a
									className={linkClass}
									href={href}
									rel={relData}
									data-width={dataWidth}
									data-href={dataHref}
									data-title={dataTitle}
								>
									<span className="text-decoration-underline">
										{linkLabel}
									</span>
								</a>
							)}
							{!!showWebsiteLink && (
								<a
									target="_blank"
									rel="noopener noreferrer"
									href={showWebsiteLink}
									className="text-link text-decoration-underline"
									data-title={labels.get(
										'ACCOUNT_LINK_WEBSITE'
									)}
								>
									<span className="text-decoration-underline">
										{labels.get('ACCOUNT_LINK_WEBSITE')}
									</span>
									<i className={iconWebsiteClasses} />
								</a>
							)}
						</div>
						<Distance />
					</div>
				</div>
			)}
		</>
	);
};

Location.propTypes = {
	displayObj: PropTypes.shape({
		icon: PropTypes.string.isRequired,
		vehicleStatusDisplay: PropTypes.string.isRequired,
		location: PropTypes.string.isRequired,
		displayStatusElement: PropTypes.string.isRequired,
		locationElement: PropTypes.string.isRequired,
		displayStatusClass: PropTypes.string.isRequired,
		displayLink: PropTypes.bool.isRequired,
		linkLabel: PropTypes.string.isRequired,
		linkClass: PropTypes.string,
		dataWidth: PropTypes.number,
		href: PropTypes.string,
		dataHref: PropTypes.string,
		relData: PropTypes.string,
		dataTitle: PropTypes.string,
		isMobile: PropTypes.bool,
		isInTransit: PropTypes.bool,
		status: PropTypes.string.isRequired
	}).isRequired,
	dealerInfo: PropTypes.shape({
		url: PropTypes.shape({
			link: PropTypes.string,
			text: PropTypes.string
		}),
		isOnPlatformAccount: PropTypes.bool
	})
};

export default Location;
